import { ProductPrice, ProductSlug } from "@/api/product-api";
import { useCurrency } from "@/hooks/useCurrency";
import { useProductPrice } from "@/hooks/useProductPrice";
import { cn } from "@/libs/utils";
import { useLocale } from "@/store/locale-store";
import { useTrans } from "@/utils/useTrans";
import { GePrice } from "@/v2-components/GePrice";

// 단종 제품: 가격 대신 "Discontinued item" 표시 (admin 변경 없이 코드로 예외 처리)
const DISCONTINUED_SLUGS: string[] = [ProductSlug.TACTOSY_FOR_FEET];

export const isDiscontinued = (slug?: string): boolean =>
  !!slug && DISCONTINUED_SLUGS.includes(slug);

// 단종 배지 (디자인: Red/10 배경 + Red/60 텍스트 pill).
// className 을 *앞에* 둔 건 의도적이다 — 호출처(PriceDisplay→TactSuitCard.Price,
// product-select 등)가 가격용 text-2xl/text-base 를 className 으로 넘기는데,
// 배지 고정 클래스를 뒤에 둬야 tailwind-merge 후자 우선으로 text-xs/text-red-60 가 이긴다.
// ⚠️ 두 가지 "정리"가 오히려 버그다:
//   1) cn 순서를 cn(badge, className) 으로 뒤집으면 호출처 text-2xl 이 다시 새 배지가 깨짐.
//   2) ui/Badge 로 교체 금지 — Badge 는 cn(variants, className) 라 호출처가 이겨 같은 leak 발생.
export const DiscontinuedLabel = ({ className }: { className?: string }) => {
  const { t } = useTrans(["tactsuit"]);
  return (
    <span
      className={cn(
        className,
        "inline-flex w-fit items-center rounded bg-red-10 px-2 py-0.5 text-xs font-600 text-red-60",
      )}
    >
      {t("Discontinued item")}
    </span>
  );
};

type Props = {
  price?: ProductPrice | null;
  className?: string;
  productSlug?: string;
};

const PriceDisplay = ({ price, className, productSlug }: Props) => {
  const { countryCode } = useLocale();
  const currency = useCurrency();
  const { displayOriginalPrice, displayDiscountAmount, displaySalePrice } =
    useProductPrice(price);

  if (isDiscontinued(productSlug)) {
    return <DiscontinuedLabel className={className} />;
  }

  if (!price) {
    return null;
  }

  return (
    <div
      className={cn(
        "flex items-center justify-center space-x-2 text-lg laptop:text-2xl",
        className,
      )}
    >
      <GePrice
        amount={displaySalePrice}
        currency={currency}
        countryCode={countryCode}
      />
      {!!displayDiscountAmount && (
        <GePrice
          className="line-through"
          amount={displayOriginalPrice}
          currency={currency}
          countryCode={countryCode}
        />
      )}
    </div>
  );
};

export default PriceDisplay;
